'use strict';

const libPictProvider = require('pict-provider');

/**
 * @typedef {{ IDCustomer: number, Name: string }} CustomerChoice
 *
 * @typedef {Object} CustomerConfigScreenState
 * @property {boolean} Loaded
 * @property {boolean} Loading
 * @property {boolean} Saving
 * @property {boolean} Dirty
 * @property {string} Error
 * @property {string} Message
 * @property {number} TargetCustomerID
 * @property {string} TargetCustomerName
 * @property {Array<CustomerChoice>} Customers
 * @property {Record<string, string>} Snapshots - `<NamespaceKey>/<NamespaceValue>` → the JSON read at load, for the stale-write check
 *
 * @typedef {Object} ScreenIdentity
 * @property {string} Hash - the provider's registered hash
 * @property {string} StateKey - the AppData key holding the screen's state
 * @property {string} Mount - the CSS selector the screen renders into
 * @property {{ Entity: string, Action: string, Label: string }} Entitlement - the capability that opens the screen, and its task label
 */

/**
 * The shared plumbing of a configuration screen that edits one customer's settings: who may use it, which customer
 * it edits (a super user picks one, everyone else edits their own), reading and writing customer-level configuration,
 * and refusing to save over a value someone else changed after the screen loaded it.
 *
 * A subclass declares a static SCREEN (ScreenIdentity), then implements `loadCustomerAsync`, `saveAsync`,
 * `bodyHTML`, and `initialState`. Saving needs a customer administrator on the configuration service; the entitlement
 * only decides who sees the screen.
 */
class CustomerConfigScreenProvider extends libPictProvider
{
	/**
	 * @param {any} pFable
	 * @param {any} pOptions
	 * @param {string} pServiceHash
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	/** @return {ScreenIdentity} who this screen is: set by each subclass as its static SCREEN */
	get screen()
	{
		return /** @type {any} */ (this.constructor).SCREEN;
	}

	/** @return {string} the provider hash the screen's inline handlers call */
	get providerHash()
	{
		return this.screen.Hash;
	}

	/** @return {string} the AppData key this screen keeps its state under */
	get stateKey()
	{
		return this.screen.StateKey;
	}

	/** @return {string} the CSS selector of the screen's detail mount */
	get mount()
	{
		return this.screen.Mount;
	}

	/** @return {{ Entity: string, Action: string, Label: string }} the entitlement that opens the screen */
	get entitlement()
	{
		return this.screen.Entitlement;
	}

	/** @return {any} the Headlight REST client */
	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	/** @return {CustomerConfigScreenState & Record<string, any>} */
	get state()
	{
		if (!this.pict.AppData[this.stateKey])
		{
			this.pict.AppData[this.stateKey] = Object.assign(
				{
					Loaded: false, Loading: false, Saving: false, Dirty: false, Error: '', Message: '',
					TargetCustomerID: 0, TargetCustomerName: '', Customers: [], Snapshots: {},
				}, this.initialState());
		}
		return this.pict.AppData[this.stateKey];
	}

	/** @return {Record<string, any>} the subclass's own state fields */
	initialState()
	{
		return {};
	}

	/** @return {Record<string, any>} the signed-in user's session */
	get currentUser()
	{
		return this.pict.AppData.CurrentUser || {};
	}

	/** @return {number} */
	callerCustomer()
	{
		return Number(this.currentUser.CustomerID) || 0;
	}

	/** @return {boolean} */
	isSuperUser()
	{
		return (Number(this.currentUser.UserRoleIndex) === 5) && (Number(this.currentUser.CustomerID) === 1);
	}

	/** @return {boolean} whether the signed-in user holds the screen's entitlement */
	can()
	{
		const tmpService = this.pict.fable.EffectivePermissionsService || (this.fable && this.fable.EffectivePermissionsService);
		if (!tmpService || (typeof tmpService.can !== 'function'))
		{
			return true;
		}
		return tmpService.can(this.entitlement.Entity, this.entitlement.Action);
	}

	/**
	 * @param {string} pURL
	 * @return {Promise<any>} the body, or null on error
	 */
	_getJSON(pURL)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve(null);
		}
		return new Promise((fResolve) => tmpClient.getJSON(pURL, (pError, pBody) => fResolve(pError ? null : pBody)));
	}

	/**
	 * @param {string} pURL
	 * @param {Object} pBody
	 * @return {Promise<{ error: any, result: any }>}
	 */
	_postJSON(pURL, pBody)
	{
		const tmpClient = this.restClient;
		if (!tmpClient)
		{
			return Promise.resolve({ error: 'no client', result: null });
		}
		return new Promise((fResolve) => tmpClient.postJSON(pURL, pBody, (pError, pResult) => fResolve({ error: pError, result: pResult })));
	}

	/**
	 * @param {any} pValue
	 * @return {string}
	 */
	_esc(pValue)
	{
		return String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[pChar]));
	}

	/**
	 * @param {any} pValue
	 * @return {string} a single-quoted JS string literal, HTML-escaped, for an inline onclick argument
	 */
	_arg(pValue)
	{
		return this._esc(JSON.stringify(String(pValue == null ? '' : pValue)).replace(/'/g, '\\u0027'));
	}

	/**
	 * Read one customer-level configuration value. A failed read throws, so a screen never offers to save over a
	 * value it could not see.
	 *
	 * @param {string} pNamespaceKey
	 * @param {string} pNamespaceValue
	 * @param {number} pIDCustomer
	 * @return {Promise<Record<string, any>>} the value, or {} when none is set
	 */
	async readCustomerConfigAsync(pNamespaceKey, pNamespaceValue, pIDCustomer)
	{
		const tmpMap = await this._getJSON(`Configuration/For/${pNamespaceKey}/In/Customer/${Number(pIDCustomer)}`);
		if (tmpMap === null)
		{
			throw new Error(`the saved ${pNamespaceKey} settings could not be read`);
		}
		const tmpValue = (tmpMap && tmpMap[pNamespaceValue] && (typeof tmpMap[pNamespaceValue] === 'object') && !Array.isArray(tmpMap[pNamespaceValue])) ? tmpMap[pNamespaceValue] : {};
		return JSON.parse(JSON.stringify(tmpValue));
	}

	/**
	 * Remember the value as read, so a save can tell whether someone else changed it in the meantime.
	 *
	 * @param {string} pNamespaceKey
	 * @param {string} pNamespaceValue
	 * @param {any} pValue
	 */
	rememberSnapshot(pNamespaceKey, pNamespaceValue, pValue)
	{
		this.state.Snapshots[`${pNamespaceKey}/${pNamespaceValue}`] = JSON.stringify(pValue || {});
	}

	/**
	 * Write one customer-level configuration value, replacing the whole value at that level, but only if the live
	 * value still equals what this screen loaded.
	 *
	 * @param {string} pNamespaceKey
	 * @param {string} pNamespaceValue
	 * @param {Record<string, any>} pValue
	 * @return {Promise<void>} rejects when the value changed since load or the write fails
	 */
	async writeCustomerConfigAsync(pNamespaceKey, pNamespaceValue, pValue)
	{
		const tmpIDCustomer = Number(this.state.TargetCustomerID);
		const tmpLive = await this.readCustomerConfigAsync(pNamespaceKey, pNamespaceValue, tmpIDCustomer);
		const tmpKey = `${pNamespaceKey}/${pNamespaceValue}`;
		if ((this.state.Snapshots[tmpKey] !== undefined) && (JSON.stringify(tmpLive) !== this.state.Snapshots[tmpKey]))
		{
			throw new Error('someone else changed these settings after you opened them. Reload the page to see their changes, then make yours again');
		}
		const tmpResult = await this._postJSON(`Configuration/Put/${pNamespaceKey}/${pNamespaceValue}/Customer/${tmpIDCustomer}`, { Config: pValue });
		if (tmpResult.error || (tmpResult.result && tmpResult.result.Error))
		{
			const tmpReason = (tmpResult.error && tmpResult.error.message) || (tmpResult.result && tmpResult.result.Error) || String(tmpResult.error);
			throw new Error(/40[13]/.test(tmpReason) ? 'saving these settings needs a customer administrator' : tmpReason);
		}
		this.rememberSnapshot(pNamespaceKey, pNamespaceValue, pValue);
	}

	/**
	 * Load the screen for the signed-in user's customer, or the customer a super user picked.
	 *
	 * @return {Promise<void>}
	 */
	async loadAsync()
	{
		const tmpState = this.state;
		tmpState.Loading = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			if (this.isSuperUser() && (tmpState.Customers.length < 1))
			{
				const tmpCustomers = await this._getJSON('Customers/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/2000');
				tmpState.Customers = Array.isArray(tmpCustomers) ? tmpCustomers.map((pCustomer) => ({ IDCustomer: Number(pCustomer.IDCustomer), Name: pCustomer.Name })) : [];
			}
			if (!tmpState.TargetCustomerID)
			{
				tmpState.TargetCustomerID = this.callerCustomer();
			}
			const tmpCustomer = await this._getJSON(`Customer/${Number(tmpState.TargetCustomerID)}`);
			tmpState.TargetCustomerName = (tmpCustomer && tmpCustomer.Name) || '';
			tmpState.Snapshots = {};
			await this.loadCustomerAsync(Number(tmpState.TargetCustomerID));
			tmpState.Dirty = false;
			tmpState.Loaded = true;
		}
		catch (pError)
		{
			tmpState.Loaded = false;
			tmpState.Error = `Could not load these settings: ${(pError && pError.message) || pError}`;
		}
		finally
		{
			tmpState.Loading = false;
			this.render();
		}
	}

	/**
	 * Read everything the screen edits for one customer into state.
	 *
	 * @param {number} pIDCustomer
	 * @return {Promise<void>}
	 */
	async loadCustomerAsync(pIDCustomer)
	{
		this.state.TargetCustomerID = Number(pIDCustomer) || 0;
	}

	/**
	 * @param {number|string} pIDCustomer
	 * @return {Promise<void>}
	 */
	async selectCustomer(pIDCustomer)
	{
		if (this.state.Dirty && (typeof window !== 'undefined') && (typeof window.confirm === 'function') && !window.confirm('You have unsaved changes. Switch customer and lose them?'))
		{
			this.render();
			return;
		}
		this.state.TargetCustomerID = Number(pIDCustomer) || 0;
		await this.loadAsync();
	}

	/** Note an unsaved edit and clear the last save's message. */
	markDirty()
	{
		this.state.Dirty = true;
		this.state.Message = '';
	}

	/**
	 * Save every value the screen edits. Subclasses write with writeCustomerConfigAsync.
	 *
	 * @return {Promise<void>}
	 */
	async saveAsync()
	{
		return Promise.resolve();
	}

	/** @return {string} the success message after a save */
	savedMessage()
	{
		return 'Saved.';
	}

	/** @return {Promise<void>} */
	async save()
	{
		const tmpState = this.state;
		if (tmpState.Saving || !tmpState.Dirty || !tmpState.Loaded)
		{
			return;
		}
		tmpState.Saving = true;
		tmpState.Error = '';
		tmpState.Message = '';
		this.render();
		try
		{
			await this.saveAsync();
			tmpState.Dirty = false;
			tmpState.Message = this.savedMessage();
		}
		catch (pError)
		{
			tmpState.Error = `Not saved: ${(pError && pError.message) || pError}.`;
		}
		finally
		{
			tmpState.Saving = false;
			this.render();
		}
	}

	/** @return {string} the customer the screen edits, as a picker for a super user */
	customerHTML()
	{
		const tmpState = this.state;
		if (this.isSuperUser() && tmpState.Customers.length)
		{
			const tmpOptions = tmpState.Customers.map((pCustomer) => `<option value="${Number(pCustomer.IDCustomer)}" ${Number(pCustomer.IDCustomer) === Number(tmpState.TargetCustomerID) ? 'selected' : ''}>${this._esc(pCustomer.Name)}</option>`).join('');
			return `<div class="ccs-field"><label class="ccs-label">Customer</label><select class="ccs-select" onchange="_Pict.providers.${this.providerHash}.selectCustomer(this.value)">${tmpOptions}</select></div>`;
		}
		return `<div class="ccs-field"><label class="ccs-label">Customer</label><span class="ccs-customer">${this._esc(tmpState.TargetCustomerName)}</span></div>`;
	}

	// --- Search pickers (lists of hundreds are searched, not listed) ----------------------------------------------

	/**
	 * The choices a picker offers for some typed text. Subclasses answer for each picker they draw.
	 *
	 * @param {string} pPicker - the picker's name
	 * @param {string} pText - what was typed
	 * @return {Array<{ Value: string, Label: string, Sub?: string }>}
	 */
	pickerChoices(pPicker, pText)
	{
		return [];
	}

	/**
	 * A choice was picked. Subclasses act on it.
	 *
	 * @param {string} pPicker
	 * @param {string} pValue
	 * @return {void}
	 */
	pickerChose(pPicker, pValue)
	{
		this.markDirty();
	}

	/**
	 * @param {string} pPicker
	 * @return {string} the element id of the picker's suggestion list
	 */
	_pickerID(pPicker)
	{
		return `CCS-${this.providerHash}-${String(pPicker).replace(/[^A-Za-z0-9_-]/g, '_')}`;
	}

	/**
	 * @param {string} pPicker
	 * @param {string} pPlaceholder
	 * @return {string} a search box whose matches list under it
	 */
	pickerHTML(pPicker, pPlaceholder)
	{
		const tmpID = this._pickerID(pPicker);
		return `<div class="ccs-picker">
			<input id="${tmpID}-Input" class="ccs-input" type="search" autocomplete="off" placeholder="${this._esc(pPlaceholder)}"
				oninput="_Pict.providers.${this.providerHash}.searchPicker(${this._arg(pPicker)}, this.value)"
				onfocus="_Pict.providers.${this.providerHash}.searchPicker(${this._arg(pPicker)}, this.value)">
			<div id="${tmpID}-Suggest" class="ccs-suggest"></div>
		</div>`;
	}

	/**
	 * Show a picker's matches without redrawing the box being typed in.
	 *
	 * @param {string} pPicker
	 * @param {string} pText
	 */
	searchPicker(pPicker, pText)
	{
		const tmpSelector = `#${this._pickerID(pPicker)}-Suggest`;
		if ((typeof document === 'undefined') || !document.querySelector(tmpSelector))
		{
			return;
		}
		this.pict.ContentAssignment.assignContent(tmpSelector, this.pickerSuggestionsHTML(pPicker, pText));
	}

	/**
	 * @param {string} pPicker
	 * @param {string} pText
	 * @return {string} up to 12 matches, and how many more there are
	 */
	pickerSuggestionsHTML(pPicker, pText)
	{
		const tmpLimit = 12;
		const tmpChoices = this.pickerChoices(pPicker, pText);
		const tmpRows = tmpChoices.slice(0, tmpLimit).map((pChoice) =>
			`<button type="button" class="ccs-suggestion" onclick="_Pict.providers.${this.providerHash}.choosePicker(${this._arg(pPicker)}, ${this._arg(pChoice.Value)})">${this._esc(pChoice.Label)}${pChoice.Sub ? ` <span class="ccs-sub">${this._esc(pChoice.Sub)}</span>` : ''}</button>`).join('');
		const tmpMore = (tmpChoices.length > tmpLimit) ? `<div class="ccs-more">${tmpChoices.length - tmpLimit} more. Keep typing to narrow the list.</div>` : '';
		if (!tmpRows)
		{
			return String(pText || '').trim() ? '<div class="ccs-more">Nothing matches.</div>' : '';
		}
		return `${tmpRows}${tmpMore}`;
	}

	/**
	 * @param {string} pPicker
	 * @param {string} pValue
	 */
	choosePicker(pPicker, pValue)
	{
		this.pickerChose(pPicker, pValue);
		this.render();
		const tmpInput = (typeof document !== 'undefined') ? document.getElementById(`${this._pickerID(pPicker)}-Input`) : null;
		if (tmpInput)
		{
			tmpInput.focus();
		}
	}

	/**
	 * @param {Array<{ Value: string, Label: string, Sub?: string }>} pChoices
	 * @param {string} pText
	 * @return {Array<{ Value: string, Label: string, Sub?: string }>} the choices whose label or detail holds every typed word
	 */
	static filterChoices(pChoices, pText)
	{
		const tmpWords = String(pText || '').toLowerCase().split(/\s+/).filter(Boolean);
		return (pChoices || []).filter((pChoice) =>
		{
			const tmpHaystack = `${pChoice.Label} ${pChoice.Sub || ''} ${pChoice.Value}`.toLowerCase();
			return tmpWords.every((pWord) => tmpHaystack.includes(pWord));
		});
	}

	/** @return {string} the save button, its unsaved-changes hint and the outcome of the last save */
	saveBarHTML()
	{
		const tmpState = this.state;
		return `<div class="ccs-savebar">
			<button class="ccs-save" ${(tmpState.Saving || !tmpState.Dirty) ? 'disabled' : ''} onclick="_Pict.providers.${this.providerHash}.save()">${tmpState.Saving ? 'Saving…' : 'Save'}</button>
			${tmpState.Dirty ? '<span class="ccs-unsaved">Unsaved changes</span>' : ''}
		</div>`;
	}

	/** @return {string} the screen's own content */
	bodyHTML()
	{
		return '';
	}

	/** @return {string} */
	renderHTML()
	{
		const tmpState = this.state;
		if (!this.can())
		{
			return `<div class="ccs-empty">You don’t have the “${this._esc(this.entitlement.Label)}” entitlement.</div>`;
		}
		if (tmpState.Loading)
		{
			return '<div class="ccs-empty"><i class="fas fa-spinner fa-spin"></i> Loading…</div>';
		}
		const tmpMessages = `${tmpState.Message ? `<div class="ccs-msg">${this._esc(tmpState.Message)}</div>` : ''}${tmpState.Error ? `<div class="ccs-error">${this._esc(tmpState.Error)}</div>` : ''}`;
		if (!tmpState.Loaded)
		{
			return `${this.customerHTML()}${tmpMessages}`;
		}
		return `${this.customerHTML()}${tmpMessages}${this.bodyHTML()}${this.saveBarHTML()}`;
	}

	/** @return {boolean} whether the screen's mount was on the page to paint */
	render()
	{
		if ((typeof document === 'undefined') || !document.querySelector(this.mount))
		{
			return false;
		}
		this.pict.ContentAssignment.assignContent(this.mount, this.renderHTML());
		return true;
	}
}

module.exports = CustomerConfigScreenProvider;
